import { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '@/lib/api-client';
import {
  entityTypeFor,
  pfgPaymentMethodFor,
  requiredFormFields,
  stepTwoFields,
} from '@/features/beneficiaries/pfg-fields';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Badge } from '@/components/data-display/Badge';
import { Select, CountrySelect, CurrencySelect } from '@/components/form';
import type { Beneficiary } from '../types';
import type { CreateBeneficiaryPayload, UpdateBeneficiaryPayload } from '../useClientDetail';
import { useLookup } from '@/hooks/useLookups';
import { validateIBAN } from '../validation';
import { toast } from '@/utils/toast';

interface BeneficiariesTabProps {
  clientId: string;
  beneficiaries: Beneficiary[];
  loading: boolean;
  onLoad: () => Promise<void>;
  onCreate: (payload: CreateBeneficiaryPayload) => Promise<void>;
  onUpdate: (id: string, payload: UpdateBeneficiaryPayload) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const BENEFICIARY_TYPE_INDIVIDUAL = 1;
const BENEFICIARY_TYPE_BUSINESS = 2;

// Form state matches CreateBeneficiaryDto (camelCase). Strings are used
// everywhere so the UI stays controlled; empty strings are coerced to
// null before submit so backend `@IsOptional` accepts them.
interface BeneficiaryForm {
  beneficiaryTypeId: string;
  label: string;
  companyName: string;
  firstName: string;
  lastName: string;
  currencyCode: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  addressCountryCode: string;
  bankName: string;
  bankCountryCode: string;
  bankNetworkId: string;
  bankCode: string;
  accountNumber: string;
  taxId: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  bankBranchCode: string;
  bankAccountType: string;
  bankBranchName: string;
  bankAddressLine1: string;
  bankAddressCity: string;
  bankAddressState: string;
  bankAddressZip: string;
}

const EMPTY_FORM: BeneficiaryForm = {
  beneficiaryTypeId: '',
  label: '',
  companyName: '',
  firstName: '',
  lastName: '',
  currencyCode: '',
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountryCode: '',
  bankName: '',
  bankCountryCode: '',
  bankNetworkId: '',
  bankCode: '',
  accountNumber: '',
  taxId: '',
  email: '',
  phoneNumber: '',
  dateOfBirth: '',
  bankBranchCode: '',
  bankAccountType: '',
  bankBranchName: '',
  bankAddressLine1: '',
  bankAddressCity: '',
  bankAddressState: '',
  bankAddressZip: '',
};

type FormErrors = Partial<Record<keyof BeneficiaryForm, string>>;

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

function buildPayload(form: BeneficiaryForm): CreateBeneficiaryPayload {
  const typeId = Number(form.beneficiaryTypeId);
  const networkId = form.bankNetworkId.trim().length > 0 ? Number(form.bankNetworkId) : null;
  return {
    beneficiaryTypeId: typeId,
    label: form.label.trim(),
    companyName: typeId === BENEFICIARY_TYPE_BUSINESS ? trimOrNull(form.companyName) : null,
    firstName: typeId === BENEFICIARY_TYPE_INDIVIDUAL ? trimOrNull(form.firstName) : null,
    lastName: typeId === BENEFICIARY_TYPE_INDIVIDUAL ? trimOrNull(form.lastName) : null,
    currencyCode: form.currencyCode.trim().toUpperCase(),
    addressLine1: trimOrNull(form.addressLine1),
    addressLine2: trimOrNull(form.addressLine2),
    addressCity: trimOrNull(form.addressCity),
    addressState: trimOrNull(form.addressState),
    addressZip: trimOrNull(form.addressZip),
    addressCountryCode: trimOrNull(form.addressCountryCode),
    bankName: trimOrNull(form.bankName),
    bankCountryCode: trimOrNull(form.bankCountryCode),
    bankNetworkId: networkId === null || Number.isNaN(networkId) ? null : networkId,
    bankCode: trimOrNull(form.bankCode),
    accountNumber: trimOrNull(form.accountNumber),
    // Champs du Payment Formatting Guide (#70). Cette charge utile est
    // construite champ par champ : un oubli ici et la saisie de
    // l'operateur est jetee avant de partir.
    taxId: trimOrNull(form.taxId),
    email: trimOrNull(form.email),
    phoneNumber: trimOrNull(form.phoneNumber),
    dateOfBirth: trimOrNull(form.dateOfBirth),
    bankBranchCode: trimOrNull(form.bankBranchCode),
    bankAccountType: trimOrNull(form.bankAccountType),
    bankBranchName: trimOrNull(form.bankBranchName),
    bankAddressLine1: trimOrNull(form.bankAddressLine1),
    bankAddressCity: trimOrNull(form.bankAddressCity),
    bankAddressState: trimOrNull(form.bankAddressState),
    bankAddressZip: trimOrNull(form.bankAddressZip),
  };
}

export function BeneficiariesTab({
  clientId: _clientId,
  beneficiaries,
  loading,
  onLoad,
  onCreate,
  onUpdate,
  onDelete,
}: BeneficiariesTabProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingBen, setEditingBen] = useState<Beneficiary | null>(null);
  const [form, setForm] = useState<BeneficiaryForm>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  // La reponse du PFG est gardee avec le corridor qui l'a produite, jamais
  // seule : les quatre parametres restent modifiables, et une liste de champs
  // detachee de son corridor exigerait des regles qui ne sont plus les
  // siennes. Cela ecarte aussi une reponse arrivee en retard (#70).
  const [corridor, setCorridor] = useState<{ key: string; fields: string[] } | null>(null);
  // L'erreur et l'attente portent elles aussi la cle de leur corridor. Sans
  // cela, revenir a un corridor deja charge laissait l'ecran sur le message
  // d'un autre, ou sur une attente que plus aucune requete ne terminerait.
  const [corridorError, setCorridorError] = useState<{ key: string; message: string } | null>(null);
  // L'etape 1 ne montre que le corridor. Une fiche existante a deja le sien,
  // donc l'edition demarre a l'etape 2 : reimposer le passage serait une
  // regression pour le geste de correction, qui est le plus frequent ici.
  const [step, setStep] = useState<1 | 2>(1);
  const [corridorLoading, setCorridorLoading] = useState<string | null>(null);
  // Relance manuelle. Apres un echec, aucune dependance de l'effet ne change :
  // sans ce compteur l'operateur n'a aucun moyen de redemander le corridor.
  const [corridorAttempt, setCorridorAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [deletingBen, setDeletingBen] = useState<Beneficiary | null>(null);

  const { items: typeOptions, loading: typesLoading } = useLookup('beneficiary-types');
  const { items: networkOptions, loading: networksLoading } = useLookup('bank-networks');

  const typeSelectOptions = useMemo(
    () => typeOptions.map((t) => ({ value: String(t.id), label: t.label })),
    [typeOptions],
  );
  const networkSelectOptions = useMemo(
    () => networkOptions.map((n) => ({ value: String(n.id), label: n.label })),
    [networkOptions],
  );
  const networkLabelById = useMemo(() => {
    const map = new Map<number, string>();
    networkOptions.forEach((n) => map.set(n.id, n.label));
    return map;
  }, [networkOptions]);

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const openCreate = useCallback(() => {
    setEditingBen(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setCorridor(null);
    setCorridorError(null);
    setCorridorLoading(null);
    setCorridorAttempt(0);
    setStep(1);
    setShowModal(true);
  }, []);

  const openEdit = useCallback((ben: Beneficiary) => {
    setEditingBen(ben);
    setForm({
      beneficiaryTypeId: ben.beneficiary_type_id != null ? String(ben.beneficiary_type_id) : '',
      label: ben.beneficiary_name,
      companyName: ben.company_name ?? '',
      firstName: ben.first_name ?? '',
      lastName: ben.last_name ?? '',
      currencyCode: ben.currency_code ?? '',
      addressLine1: ben.address_line1 ?? '',
      addressLine2: ben.address_line2 ?? '',
      addressCity: ben.address_city ?? '',
      addressState: ben.address_state ?? '',
      addressZip: ben.address_zip ?? '',
      addressCountryCode: ben.address_country_code ?? '',
      bankName: ben.bank_name ?? '',
      bankCountryCode: ben.bank_country_code ?? '',
      bankNetworkId: ben.bank_network_id != null ? String(ben.bank_network_id) : '',
      bankCode: ben.bank_code ?? ben.bic ?? '',
      accountNumber: ben.account_number,
      taxId: ben.tax_id ?? '',
      email: ben.email ?? '',
      phoneNumber: ben.phone_number ?? '',
      dateOfBirth: ben.date_of_birth ?? '',
      bankBranchCode: ben.bank_branch_code ?? '',
      bankAccountType: ben.bank_account_type ?? '',
      bankBranchName: ben.bank_branch_name ?? '',
      bankAddressLine1: ben.bank_address_line1 ?? '',
      bankAddressCity: ben.bank_address_city ?? '',
      bankAddressState: ben.bank_address_state ?? '',
      bankAddressZip: ben.bank_address_zip ?? '',
    });
    setFormErrors({});
    setStep(2);
    setShowModal(true);
  }, []);

  // Le rail (#153) est le cinquieme parametre du corridor : Wire ou Direct
  // Credit n'exigent pas les memes champs, un SEPA n'a pas besoin de BIC.
  const railCode =
    networkOptions.find((n) => String(n.id) === form.bankNetworkId.trim())?.code ?? '';

  const corridorKey = [
    form.currencyCode.trim().toUpperCase(),
    form.bankCountryCode.trim().toUpperCase(),
    form.addressCountryCode.trim().toUpperCase(),
    entityTypeFor(Number(form.beneficiaryTypeId)) ?? '',
    railCode,
  ].join('|');

  const corridorComplete = corridorKey.split('|').every((part) => part.length > 0);
  // Les cinq saisies sont-elles remplies ? Distinct de `corridorComplete`,
  // qui exige en plus un type de beneficiaire que Convera sache nommer.
  const corridorInputsFilled = [
    form.beneficiaryTypeId,
    form.currencyCode,
    form.bankCountryCode,
    form.addressCountryCode,
    form.bankNetworkId,
  ].every((value) => value.trim().length > 0);
  const pfgFields = corridor && corridor.key === corridorKey ? corridor.fields : null;
  /** Les cinq saisies sont la, mais Convera n'a pas (encore) dit ce qu'il exige. */
  const corridorUnknown = corridorInputsFilled && !pfgFields;

  /**
   * Le rail est saisi mais le referentiel ne lui connait pas de code — une
   * fiche d'avant #153, ou un reseau retire depuis. `corridorComplete` reste
   * faux, donc aucune requete ne part : reessayer n'y changerait rien, il faut
   * choisir un autre rail.
   */
  const railUnresolved = form.bankNetworkId.trim().length > 0 && railCode === '';

  /**
   * L'operateur a-t-il touche au corridor de la fiche qu'il modifie ?
   *
   * Type, devise et rail sont figes en edition, mais les deux pays restent
   * modifiables : en changer un fabrique un corridor que Convera n'a jamais
   * valide. L'exemption accordee a l'edition ne vaut que tant qu'on reste sur
   * le corridor d'origine.
   */
  const sameCode = (a: string | null | undefined, b: string | null | undefined) =>
    (a ?? '').trim().toUpperCase() === (b ?? '').trim().toUpperCase();
  const corridorTouched =
    editingBen !== null &&
    (!sameCode(form.bankCountryCode, editingBen.bank_country_code) ||
      !sameCode(form.addressCountryCode, editingBen.address_country_code));
  const corridorErrorMessage = corridorError?.key === corridorKey ? corridorError.message : null;
  const corridorPending = corridorLoading === corridorKey;

  /**
   * Interroge le PFG des que les quatre parametres du corridor sont connus.
   *
   * Sans bouton : l'operateur corrige souvent une fiche existante, et lui
   * imposer une etape supplementaire ralentirait le geste pour lequel cet
   * ecran existe.
   *
   * En cas d'echec, on bloque. La premiere version retombait sur « tout
   * afficher, rien exiger » pour ne pas empecher de travailler si Convera
   * etait indisponible — mais cela produit un beneficiaire que Convera
   * refusera au premier paiement, sans que personne l'ait vu passer. Le PFG
   * dit ce que le corridor exige : sans lui, on ne sait pas si la fiche est
   * valide, et le supposer n'aide personne.
   */
  useEffect(() => {
    if (!corridorComplete || (corridor && corridor.key === corridorKey)) return;
    const requestedKey = corridorKey;
    const [currencyCode, beneBankCountryCode, countryCode, entityType, rail] =
      requestedKey.split('|');
    let cancelled = false;

    setCorridorError(null);
    setCorridorLoading(requestedKey);

    void api
      .post<{ requiredFields: string[] }>('/api/pfg/fields', {
        beneBankCountryCode,
        currencyCode,
        countryCode,
        entityType,
        paymentMethod: pfgPaymentMethodFor(rail),
      })
      .then((res) => {
        if (cancelled) return;
        if (!Array.isArray(res?.requiredFields)) {
          // Une reponse dont on ne sait pas lire la forme n'est pas un
          // corridor sans exigence : la traiter comme telle laisserait
          // enregistrer une fiche vide de tout ce que Convera reclame.
          setCorridorError({
            key: requestedKey,
            message: 'Convera returned an unreadable response.',
          });
          return;
        }
        setCorridor({ key: requestedKey, fields: res.requiredFields });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCorridorError({
          key: requestedKey,
          message: err instanceof Error ? err.message : 'Convera could not be reached.',
        });
      })
      .finally(() => {
        if (!cancelled) setCorridorLoading(null);
      });

    return () => {
      cancelled = true;
    };
  }, [corridorKey, corridorComplete, corridor, corridorAttempt]);

  const corridorRequired = pfgFields ? requiredFormFields(pfgFields) : [];
  const corridorShown = pfgFields ? stepTwoFields(pfgFields) : null;

  /**
   * Un champ du PFG est-il visible ?
   *
   * Tant que le corridor n'est pas connu, on n'affiche rien : montrer les
   * onze champs laisserait croire qu'ils sont tous a remplir, alors qu'un
   * corridor n'en reclame souvent que deux ou trois.
   */
  const showsPfgField = (field: string) => corridorShown !== null && corridorShown.includes(field);

  // Le meme avertissement aux deux etapes : les deux pays restent modifiables
  // a l'etape 2, et un echec y faisait disparaitre les champs du corridor sans
  // rien dire, l'operateur ne l'apprenant qu'au moment d'enregistrer.
  const corridorNotice = corridorErrorMessage ? (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <p className="text-[11px] text-[#c62828]">Convera unavailable — {corridorErrorMessage}</p>
      <button
        type="button"
        onClick={() => setCorridorAttempt((attempt) => attempt + 1)}
        disabled={corridorPending}
        className="text-[11px] font-semibold text-[#1565c0] underline disabled:cursor-not-allowed disabled:text-[#999]"
      >
        {corridorPending ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  ) : null;

  const validate = (): boolean => {
    const errors: FormErrors = {};
    const typeId = Number(form.beneficiaryTypeId);
    if (!form.beneficiaryTypeId) {
      errors.beneficiaryTypeId = 'Beneficiary type is required';
    }
    if (!form.label.trim()) errors.label = 'Label is required';
    else if (form.label.trim().length > 200) errors.label = 'Label must be 200 characters or less';
    if (typeId === BENEFICIARY_TYPE_BUSINESS && !form.companyName.trim()) {
      errors.companyName = 'Company name is required for business beneficiaries';
    }
    if (typeId === BENEFICIARY_TYPE_INDIVIDUAL) {
      if (!form.firstName.trim())
        errors.firstName = 'First name is required for individual beneficiaries';
      if (!form.lastName.trim())
        errors.lastName = 'Last name is required for individual beneficiaries';
    }
    if (!form.currencyCode.trim()) {
      errors.currencyCode = 'Currency is required';
    } else if (!/^[A-Z]{3}$/.test(form.currencyCode.trim().toUpperCase())) {
      errors.currencyCode = 'Currency must be a 3-letter ISO code';
    }
    // Sans corridor, on n'enregistre pas.
    //
    // Le PFG dit ce que Convera exige pour cette destination. Tant qu'on ne
    // l'a pas, on ignore si la fiche est complete — et l'enregistrer quand
    // meme deplace le refus au premier paiement, la ou il coute le plus cher.
    if (!form.addressCountryCode.trim()) {
      errors.addressCountryCode = 'Beneficiary country is required';
    }
    if (!form.bankCountryCode.trim()) {
      errors.bankCountryCode = 'Bank country is required';
    }
    // Le corridor inconnu bloquait en posant une erreur sur `currencyCode` —
    // un champ desactive en edition, donc une erreur que personne ne voyait :
    // le bouton Enregistrer ne faisait rien, sans un mot (#190).
    //
    // A la creation, le blocage reste : l'operateur peut changer de corridor.
    // En edition, type, devise et rail sont figes ; il n'a aucun moyen de
    // reparer, et la fiche a deja passe le PFG a sa creation. On l'avertit,
    // on ne l'enferme pas.
    // A la creation, l'operateur peut changer de corridor : le blocage garde
    // son sens. En edition il ne le peut pas — sauf s'il vient justement d'en
    // changer un pays, auquel cas la fiche n'a plus rien de valide.
    const corridorBlocks = corridorUnknown && (!editingBen || corridorTouched);

    // Ce que le corridor exige, en plus des regles ci-dessus. La liste vient
    // de Convera : exiger autre chose serait deviner, et n'exiger rien
    // laisserait partir un paiement que Convera refusera.
    for (const field of corridorRequired) {
      const value = (form as unknown as Record<string, string>)[field];
      if (typeof value === 'string' && value.trim() === '') {
        (errors as unknown as Record<string, string>)[field] = 'Required for this payment corridor';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0 && !corridorBlocks;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload(form);
      if (editingBen) {
        const { beneficiaryTypeId, currencyCode, bankNetworkId, ...updatePayload } = payload;
        await onUpdate(editingBen.id, updatePayload);
      } else {
        await onCreate(payload);
      }
      setShowModal(false);
    } catch {
      // Error already toasted by hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingBen) return;
    try {
      await onDelete(deletingBen.id);
    } catch {
      // Error already toasted
    }
    setDeletingBen(null);
  };

  const updateField = <K extends keyof BeneficiaryForm>(field: K, value: BeneficiaryForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Soft warning when the typed account number doesn't checksum as a valid IBAN.
  // The backend accepts any account_number — this is a hint for the operator only.
  const accountWarning = useMemo(() => {
    const trimmed = form.accountNumber.trim();
    if (trimmed.length === 0) return null;
    if (/^[A-Z]{2}\d/i.test(trimmed) && !validateIBAN(trimmed)) {
      return 'Looks like an IBAN but the checksum is invalid. Double-check before saving.';
    }
    return null;
  }, [form.accountNumber]);

  const typeId = Number(form.beneficiaryTypeId);

  if (loading) {
    return <LoadingSpinner size="md" text="Loading beneficiaries..." />;
  }

  return (
    <div>
      <div className="mb-4">
        <Button size="sm" className="bg-[#1565c0] hover:bg-[#0d47a1]" onClick={openCreate}>
          + Add Beneficiary
        </Button>
      </div>

      {beneficiaries.length === 0 ? (
        <div className="rounded-xl border border-[#eee] bg-white p-6 text-center text-[#999]">
          No beneficiaries registered
        </div>
      ) : (
        <div className="space-y-2.5">
          {beneficiaries.map((ben) => {
            const networkLabel =
              ben.bank_network_id != null ? networkLabelById.get(ben.bank_network_id) : undefined;
            return (
              <div key={ben.id} className="rounded-xl border border-[#eee] bg-white p-[18px]">
                <div className="flex items-center justify-between">
                  <div>
                    <div
                      className="text-[14px] font-bold text-[#1a1a1a]"
                      title={ben.beneficiary_name}
                    >
                      {ben.beneficiary_name.length > 100
                        ? `${ben.beneficiary_name.slice(0, 100)}...`
                        : ben.beneficiary_name}
                    </div>
                    <div className="mt-0.5 text-[12px] text-[#999]">
                      {[ben.bank_name, ben.bank_code ?? ben.bic, ben.account_number]
                        .filter(Boolean)
                        .join(' \u00B7 ') || 'No banking details'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="info">{ben.country_code || '—'}</Badge>
                    <Badge variant={ben.is_active ? 'success' : 'default'}>
                      {ben.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => openEdit(ben)}>
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[#c62828]"
                      onClick={() => setDeletingBen(ben)}
                    >
                      Deactivate
                    </Button>
                  </div>
                </div>
                <div className="mt-3 border-t border-[#f5f5f5] pt-2.5">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        {ben.iban ? 'IBAN' : 'Account number'}
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">
                        {ben.iban || ben.account_number || '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        Currency
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">{ben.currency_code || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        Bank Code
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">
                        {ben.bank_code || ben.bic || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-2.5 grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        Bank Name
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">{ben.bank_name || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        Bank Country
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">
                        {ben.bank_country_code || '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                        Bank Network
                      </div>
                      <div className="text-[13px] text-[#1a1a1a]">
                        {networkLabel ??
                          (ben.bank_network_id != null ? `#${ben.bank_network_id}` : '—')}
                      </div>
                    </div>
                  </div>

                  {/* The routing code (ABA, transit) has its own column:
                      bank_code only holds the BIC (#299). */}
                  {ben.bank_branch_code && (
                    <div className="mt-2.5 grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                          Routing / ABA Code
                        </div>
                        <div className="text-[13px] text-[#1a1a1a]">{ben.bank_branch_code}</div>
                      </div>
                    </div>
                  )}

                  {(ben.address_line1 || ben.address_city || ben.address_country_code) && (
                    <div className="mt-2.5 grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                          Address
                        </div>
                        <div className="text-[13px] text-[#1a1a1a]">
                          {[
                            ben.address_line1,
                            ben.address_line2,
                            [ben.address_zip, ben.address_city].filter(Boolean).join(' '),
                            ben.address_state,
                          ]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.5px] text-[#999]">
                          Country
                        </div>
                        <div className="text-[13px] text-[#1a1a1a]">
                          {ben.address_country_code || '—'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        title={editingBen ? 'Edit Beneficiary' : 'Add Beneficiary'}
        onClose={() => setShowModal(false)}
        className="max-w-2xl"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => (step === 2 && !editingBen ? setStep(1) : setShowModal(false))}
            >
              {step === 2 && !editingBen ? 'Back' : 'Cancel'}
            </Button>
            {step === 1 ? (
              <Button
                className="bg-[#1565c0] hover:bg-[#0d47a1]"
                // Le corridor doit etre connu avant d'aller plus loin : c'est
                // lui qui dit quels champs Convera reclame.
                onClick={() => setStep(2)}
                disabled={!corridorComplete || corridorPending || !pfgFields}
              >
                {corridorPending ? 'Asking Convera…' : 'Next'}
              </Button>
            ) : (
              <Button
                className="bg-[#1565c0] hover:bg-[#0d47a1]"
                onClick={() => void handleSubmit()}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Beneficiary'}
              </Button>
            )}
          </>
        }
      >
        <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
          {/* Ce que Convera exige pour ce corridor n'a pas pu etre lu. Le dire
              ici et non sur un champ : en edition les champs du corridor sont
              desactives, et l'erreur y restait invisible (#190). */}
          {corridorUnknown && !corridorPending && (
            <div
              role="alert"
              data-testid="beneficiary-corridor-warning"
              className={`rounded-lg border px-4 py-3 text-sm ${
                editingBen && !corridorTouched && !railUnresolved
                  ? 'border-[#f0c36d] bg-[#fdf6e3] text-[#7a5b00]'
                  : 'border-[#c62828] bg-[#fdecea] text-[#8b1a12]'
              }`}
            >
              {railUnresolved ? (
                <>
                  <p>This payment rail is no longer offered for beneficiaries.</p>
                  {/* Rien a reessayer : `corridorComplete` reste faux, aucune
                      requete n'est partie et aucune ne partira. */}
                  <p className="mt-1 text-[12px]">
                    Convera cannot be asked what this corridor requires until another rail is
                    picked.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    {corridorErrorMessage
                      ? `Convera field requirements unavailable: ${corridorErrorMessage}`
                      : 'Convera field requirements could not be read for this corridor.'}
                  </p>
                  <p className="mt-1 text-[12px]">
                    {editingBen && !corridorTouched
                      ? 'Saving is still possible: the corridor cannot be changed here, and it was checked when the beneficiary was created.'
                      : 'Pick a corridor Convera knows, or retry.'}
                  </p>
                  <button
                    type="button"
                    className="mt-2 text-[12px] font-semibold underline"
                    onClick={() => setCorridorAttempt((n) => n + 1)}
                  >
                    Retry
                  </button>
                </>
              )}
            </div>
          )}

          {/* Type */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Type
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Beneficiary Type *
                </label>
                <Select
                  value={form.beneficiaryTypeId}
                  onChange={(v) => updateField('beneficiaryTypeId', v)}
                  options={typeSelectOptions}
                  placeholder={typesLoading ? 'Loading types…' : 'Select type…'}
                  error={formErrors.beneficiaryTypeId}
                  disabled={typesLoading || !!editingBen}
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">Label *</label>
                <Input
                  value={form.label}
                  onChange={(v) => updateField('label', v)}
                  error={formErrors.label}
                  placeholder="Acme Corp — EUR"
                />
              </div>
            </div>
          </section>

          {/* Identity */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Identity
            </h3>
            {typeId === BENEFICIARY_TYPE_BUSINESS && (
              <div>
                <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                  Company Name *
                </label>
                <Input
                  value={form.companyName}
                  onChange={(v) => updateField('companyName', v)}
                  error={formErrors.companyName}
                />
              </div>
            )}
            {typeId === BENEFICIARY_TYPE_INDIVIDUAL && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    First Name *
                  </label>
                  <Input
                    value={form.firstName}
                    onChange={(v) => updateField('firstName', v)}
                    error={formErrors.firstName}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    Last Name *
                  </label>
                  <Input
                    value={form.lastName}
                    onChange={(v) => updateField('lastName', v)}
                    error={formErrors.lastName}
                  />
                </div>
              </div>
            )}
            {!typeId && (
              <p className="text-[12px] text-[#999]">
                Select a beneficiary type to fill in identity details.
              </p>
            )}
          </section>

          {/* Currency */}
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
              Currency
            </h3>
            <div>
              <label className="mb-1 block text-[12px] font-semibold text-[#666]">Currency *</label>
              <CurrencySelect
                value={form.currencyCode}
                onChange={(v) => updateField('currencyCode', v)}
                error={formErrors.currencyCode}
                disabled={!!editingBen}
              />
            </div>
          </section>

          {/* Address */}
          {/* Etape 1 : les quatre parametres qui definissent le corridor.
              Le type et le nom sont au-dessus ; les deux pays sont ici, sortis
              des sections Adresse et Banque ou ils se trouvent a l'etape 2.
              Les y laisser obligeait l'operateur a deviner qu'il fallait ces
              quatre-la pour que le reste apparaisse — et personne ne l'a
              devine. */}
          {step === 1 && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
                Payment Route
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    Beneficiary Country
                  </label>
                  <CountrySelect
                    value={form.addressCountryCode}
                    onChange={(v) => updateField('addressCountryCode', v)}
                    error={formErrors.addressCountryCode}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    Bank Country
                  </label>
                  <CountrySelect
                    value={form.bankCountryCode}
                    onChange={(v) => updateField('bankCountryCode', v)}
                    error={formErrors.bankCountryCode}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                    Payment Rail
                  </label>
                  <Select
                    value={form.bankNetworkId}
                    onChange={(v) => updateField('bankNetworkId', v)}
                    options={networkSelectOptions}
                    placeholder={networksLoading ? 'Loading rails…' : 'Select rail…'}
                    disabled={networksLoading || !!editingBen}
                  />
                  <p className="mt-1 text-[11px] text-[#888]">
                    Wire is SWIFT. Direct Credit is the local network of the bank country (SEPA,
                    ACH…), resolved by Convera.
                  </p>
                </div>
              </div>
              {corridorNotice}
            </section>
          )}

          {/* Etape 2 : ce que le corridor impose, plus l'adresse et la banque.
              Rien de tout cela n'a de sens avant de savoir ce que Convera
              reclame pour cette destination. */}
          {step === 2 && (
            <>
              {corridorNotice}
              <section>
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
                  Beneficiary Address
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Address Line 1
                    </label>
                    <Input
                      value={form.addressLine1}
                      onChange={(v) => updateField('addressLine1', v)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Address Line 2
                    </label>
                    <Input
                      value={form.addressLine2}
                      onChange={(v) => updateField('addressLine2', v)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">City</label>
                    <Input
                      value={form.addressCity}
                      onChange={(v) => updateField('addressCity', v)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      State / Region
                    </label>
                    <Input
                      value={form.addressState}
                      onChange={(v) => updateField('addressState', v)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Postal Code
                    </label>
                    <Input value={form.addressZip} onChange={(v) => updateField('addressZip', v)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Country
                    </label>
                    <CountrySelect
                      value={form.addressCountryCode}
                      onChange={(v) => updateField('addressCountryCode', v)}
                      error={formErrors.addressCountryCode}
                    />
                  </div>
                </div>
              </section>

              {/* Bank */}
              <section>
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
                  Bank
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Bank Name
                    </label>
                    <Input value={form.bankName} onChange={(v) => updateField('bankName', v)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Bank Country
                    </label>
                    <CountrySelect
                      value={form.bankCountryCode}
                      onChange={(v) => updateField('bankCountryCode', v)}
                      error={formErrors.bankCountryCode}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Bank Code (SWIFT / BIC)
                    </label>
                    <Input
                      value={form.bankCode}
                      onChange={(v) => updateField('bankCode', v)}
                      placeholder="BCEELULL"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                      Account Number / IBAN
                    </label>
                    <Input
                      value={form.accountNumber}
                      onChange={(v) => updateField('accountNumber', v)}
                      error={formErrors.accountNumber}
                    />
                  </div>
                </div>
                {accountWarning && (
                  <div className="mt-2 rounded-md border border-[#f0c36d] bg-[#fff8e1] px-3 py-2 text-[12px] text-[#8a6d00]">
                    {accountWarning}
                  </div>
                )}
              </section>

              {/* Champs exiges par le corridor (#70). Tant que le PFG n'a pas
              repondu, tout est montre et rien n'est exige : on ne bloque pas
              une correction parce que Convera est indisponible. */}
              <section>
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#999]">
                  Corridor Details
                  {corridorShown !== null && (
                    <span className="ml-2 font-normal normal-case tracking-normal text-[#2e7d32]">
                      {corridorRequired.length} field
                      {corridorRequired.length === 1 ? '' : 's'} required by Convera for this route
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {showsPfgField('taxId') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Tax ID
                        {corridorRequired.includes('taxId') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.taxId}
                        onChange={(v) => updateField('taxId', v)}
                        maxLength={35}
                      />
                      {formErrors.taxId && (
                        <p className="mt-1 text-[11px] text-[#c62828]">{formErrors.taxId}</p>
                      )}
                    </div>
                  )}
                  {showsPfgField('email') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Email
                        {corridorRequired.includes('email') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.email}
                        onChange={(v) => updateField('email', v)}
                        maxLength={80}
                      />
                      {formErrors.email && (
                        <p className="mt-1 text-[11px] text-[#c62828]">{formErrors.email}</p>
                      )}
                    </div>
                  )}
                  {showsPfgField('phoneNumber') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Phone Number
                        {corridorRequired.includes('phoneNumber') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.phoneNumber}
                        onChange={(v) => updateField('phoneNumber', v)}
                        maxLength={20}
                      />
                      {formErrors.phoneNumber && (
                        <p className="mt-1 text-[11px] text-[#c62828]">{formErrors.phoneNumber}</p>
                      )}
                    </div>
                  )}
                  {showsPfgField('dateOfBirth') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Date of Birth
                        {corridorRequired.includes('dateOfBirth') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <input
                        type="date"
                        className="h-9 w-full rounded border border-[#ddd] px-2 text-[13px]"
                        value={form.dateOfBirth}
                        onChange={(e) => updateField('dateOfBirth', e.target.value)}
                      />
                      {formErrors.dateOfBirth && (
                        <p className="mt-1 text-[11px] text-[#c62828]">{formErrors.dateOfBirth}</p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankBranchCode') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Routing / ABA Code
                        {corridorRequired.includes('bankBranchCode') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankBranchCode}
                        onChange={(v) => updateField('bankBranchCode', v)}
                        maxLength={50}
                      />
                      {formErrors.bankBranchCode && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankBranchCode}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankAccountType') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Account Type
                        {corridorRequired.includes('bankAccountType') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <select
                        className="h-9 w-full rounded border border-[#ddd] px-2 text-[13px]"
                        value={form.bankAccountType}
                        onChange={(e) => updateField('bankAccountType', e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="Checking">Checking</option>
                        <option value="Savings">Savings</option>
                      </select>
                      {formErrors.bankAccountType && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankAccountType}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankBranchName') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Branch Name
                        {corridorRequired.includes('bankBranchName') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankBranchName}
                        onChange={(v) => updateField('bankBranchName', v)}
                        maxLength={80}
                      />
                      {formErrors.bankBranchName && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankBranchName}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankAddressLine1') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Bank Address Line 1
                        {corridorRequired.includes('bankAddressLine1') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankAddressLine1}
                        onChange={(v) => updateField('bankAddressLine1', v)}
                        maxLength={35}
                      />
                      {formErrors.bankAddressLine1 && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankAddressLine1}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankAddressCity') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Bank City
                        {corridorRequired.includes('bankAddressCity') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankAddressCity}
                        onChange={(v) => updateField('bankAddressCity', v)}
                        maxLength={35}
                      />
                      {formErrors.bankAddressCity && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankAddressCity}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankAddressState') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Bank State / Province
                        {corridorRequired.includes('bankAddressState') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankAddressState}
                        onChange={(v) => updateField('bankAddressState', v)}
                        maxLength={35}
                      />
                      {formErrors.bankAddressState && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankAddressState}
                        </p>
                      )}
                    </div>
                  )}
                  {showsPfgField('bankAddressZip') && (
                    <div>
                      <label className="mb-1 block text-[12px] font-semibold text-[#666]">
                        Bank Postal Code
                        {corridorRequired.includes('bankAddressZip') && (
                          <span className="text-[#c62828]"> *</span>
                        )}
                      </label>
                      <Input
                        value={form.bankAddressZip}
                        onChange={(v) => updateField('bankAddressZip', v)}
                        maxLength={16}
                      />
                      {formErrors.bankAddressZip && (
                        <p className="mt-1 text-[11px] text-[#c62828]">
                          {formErrors.bankAddressZip}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deletingBen !== null}
        title="Deactivate Beneficiary"
        message={
          deletingBen
            ? `Are you sure you want to deactivate "${deletingBen.beneficiary_name}"?`
            : ''
        }
        confirmText="Deactivate"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeletingBen(null)}
        isDangerous
      />
    </div>
  );
}
